import { type FocusEvent, useCallback, useState } from 'react';
import type { Key } from 'react-aria-components';
import { AddressFields } from '@/components/AddressFields';
import { FormattedAddress } from '@/components/FormattedAddress';
import { Heading } from '@/components/Heading';
import { CloseXButton, Modal } from '@/components/Modal';
import {
  FieldName,
  ValidationMessage,
  ValidationSectionKey,
} from '@/constants';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import type { RequiredFormField } from '@/contexts/types';
import { useUser } from '@/contexts/userContext';
import { useValidation } from '@/contexts/validationContext';
import { useGetCounties } from '@/hooks/useGetCounties';
import type { Address, AddressFieldKey, Handler } from '@/types';
import { getCountyLabel } from '@/utils/getCountyLabel';
import { getPostalCodeLabel } from '@/utils/getPostalCodeLabel';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';
import { validatePhone } from '@/utils/validatePhone';
import { getAddressData } from './getAddressData';
import { useUpdateAddress } from './useUpdateAddress';

interface GetAddressFieldProps {
  address: Address;
  isNew?: boolean;
}

export const useAddressFields = () => {
  const { countries } = usePageData();
  const { setDeliveryAddress, setStoredAddressIds } = useShipping();
  const { deliveryAddresses } = useUser();
  const { isPending, updateAddress } = useUpdateAddress();

  const [countryCode, setCountryCode] = useState('');

  const counties = useGetCounties(countryCode);
  const countyLabel = getCountyLabel(countryCode);
  const postalCodeLabel = getPostalCodeLabel(countryCode);

  const {
    addressFieldErrors,
    clearAddressFieldError,
    errorMessages,
    setAddressFieldError,
  } = useValidation();

  const addressError = errorMessages[ValidationSectionKey.ShippingAddress];

  const validationErrors = addressFieldErrors.delivery;

  const updateValidation = useCallback(
    (fieldName: RequiredFormField, value: string) => {
      // Phone validation: check format and length
      if (fieldName === FieldName.Phone) {
        if (!validatePhone(value)) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.InvalidPhone,
          );
        } else if (value.length > 19) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.PhoneTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.FirstName) {
        if (value.length > 20) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.FirstNameTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.LastName) {
        if (value.length > 20) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.LastNameTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.AddressLine1) {
        if (value.length > 50) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.AddressLineTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.City) {
        if (value.length > 50) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.CityTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      }
      // For other fields (postalCode, countryCode, etc.), don't clear errors here
      // They have their own validation logic
    },
    [clearAddressFieldError, setAddressFieldError],
  );

  const handlePhoneBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (value) {
        updateValidation(FieldName.Phone, value);
      } else {
        setAddressFieldError(
          'delivery',
          FieldName.Phone,
          ValidationMessage.MissingField,
        );
      }
    },
    [setAddressFieldError, updateValidation],
  );

  const handleChange = useCallback(
    (fieldName: AddressFieldKey) => (value: Key | null) => {
      const rawValue = typeof value === 'string' ? value : '';
      const normalisedValue = rawValue.trim();

      // Validate raw length (including spaces) before trimming for length-constrained fields
      if (
        fieldName === FieldName.FirstName ||
        fieldName === FieldName.LastName ||
        fieldName === FieldName.Phone ||
        fieldName === FieldName.AddressLine1 ||
        fieldName === FieldName.City
      ) {
        let errorMessage: string | null = null;
        if (fieldName === FieldName.FirstName && rawValue.length > 20) {
          errorMessage = ValidationMessage.FirstNameTooLong;
        } else if (fieldName === FieldName.LastName && rawValue.length > 20) {
          errorMessage = ValidationMessage.LastNameTooLong;
        } else if (fieldName === FieldName.Phone && rawValue.length > 19) {
          errorMessage = ValidationMessage.PhoneTooLong;
        } else if (
          fieldName === FieldName.AddressLine1 &&
          rawValue.length > 50
        ) {
          errorMessage = ValidationMessage.AddressLineTooLong;
        } else if (fieldName === FieldName.City && rawValue.length > 50) {
          errorMessage = ValidationMessage.CityTooLong;
        }

        if (errorMessage) {
          setAddressFieldError(
            'delivery',
            fieldName as RequiredFormField,
            errorMessage,
          );
        }
      }

      const requiredFields = getRequiredAddressFields(countryCode);

      const isRequired = requiredFields.some(key => key === fieldName);

      // Validation only for required fields
      if (isRequired) {
        if (normalisedValue) {
          // For length-constrained fields, use raw value for length validation
          // For phone, always use raw value to check total length including formatting
          const isLengthConstrained =
            fieldName === FieldName.FirstName ||
            fieldName === FieldName.LastName ||
            fieldName === FieldName.Phone ||
            fieldName === FieldName.AddressLine1 ||
            fieldName === FieldName.City;
          const valueToValidate =
            fieldName === FieldName.Phone || isLengthConstrained
              ? rawValue
              : normalisedValue;
          updateValidation(fieldName as RequiredFormField, valueToValidate);
        } else {
          setAddressFieldError(
            'delivery',
            fieldName as RequiredFormField,
            ValidationMessage.MissingField,
          );
        }
      }
      // Note: Error clearing for length-constrained fields is handled by updateValidation
      // No need to clear errors here to avoid conflicts

      // Address update for ALL fields (required and optional)
      if (fieldName === FieldName.Country) {
        const selectedCountry = countries?.find(
          country => value === country.countryCode,
        );
        if (selectedCountry) {
          setCountryCode(selectedCountry.countryCode);
          // When country changes, also clear any previously selected county
          // so the user must select a valid county for the new country
          setDeliveryAddress({
            [FieldName.Country]: selectedCountry.countryCode,
            [FieldName.County]: '',
          });
        }
      } else if (fieldName === FieldName.County) {
        const selectedCounty = counties?.find(
          county => value === county.countyCode,
        );
        if (selectedCounty) {
          setDeliveryAddress({
            [FieldName.County]: selectedCounty.countyCode,
          });
        }
      } else {
        // Store trimmed value for text fields to avoid storing leading/trailing spaces
        const valueToStore =
          typeof value === 'string' ? normalisedValue : value;
        setDeliveryAddress({ [fieldName]: valueToStore });
      }
    },
    [
      counties,
      countries,
      countryCode,
      clearAddressFieldError,
      setAddressFieldError,
      setDeliveryAddress,
      updateValidation,
    ],
  );

  const deliveryAddressIds = deliveryAddresses.map(({ id }) => id ?? '');

  const handleSubmit = useCallback(
    (close: () => void, isNew: boolean): Handler =>
      e => {
        e.preventDefault();

        if (!isPending) {
          if (isNew) {
            setStoredAddressIds(deliveryAddressIds);
          }

          const data = getAddressData(e.currentTarget);

          updateAddress(data, {
            onSuccess: close,
          });
        }
      },
    [deliveryAddressIds, isPending, setStoredAddressIds, updateAddress],
  );

  return {
    getAddressField: ({ address, isNew = false }: GetAddressFieldProps) => {
      const title = isNew ? 'Enter a new address' : 'Edit address';
      const triggerLabel = isNew ? 'Enter a new address' : 'Edit';

      const onOpenChange = (isOpen: boolean) => {
        if (isOpen) {
          setCountryCode(address.countryCode);
        }
      };

      return {
        cta: (
          <Modal
            isLinkLike={true}
            onOpenChange={onOpenChange}
            trigger={{ content: triggerLabel, label: title }}
          >
            {({ close }) => (
              <>
                <Heading level={3} slot="title">
                  {title}
                </Heading>
                {addressError ? (
                  <p className="validation-error">{addressError}</p>
                ) : null}
                <AddressFields
                  address={address}
                  counties={counties}
                  countyLabel={countyLabel}
                  handleChange={handleChange}
                  handlePhoneBlur={handlePhoneBlur}
                  handleSubmit={handleSubmit(close, isNew)}
                  isModal={true}
                  isPending={isPending}
                  postalCodeLabel={postalCodeLabel}
                  validationErrors={validationErrors}
                />
                <CloseXButton className="close-modal" slot="close" />
              </>
            )}
          </Modal>
        ),
        label: <FormattedAddress addressData={address} />,
        value: address.id ?? '',
      };
    },
  };
};
